import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore'
import { db } from '../config/firebase'

/**
 * Get all announcements ordered by posted date (newest first)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getAllAnnouncements = async () => {
  try {
    const announcementsQuery = query(
      collection(db, 'announcements'),
      orderBy('postedAt', 'desc')
    )

    const querySnapshot = await getDocs(announcementsQuery)
    const announcements = []

    querySnapshot.forEach((doc) => {
      announcements.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: announcements }
  } catch (error) {
    console.error('Get announcements error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Create new announcement
 * @param {object} data - Announcement data
 * @returns {Promise<{ success: boolean, docId?: string, data?: object, error?: string }>}
 */
export const createAnnouncement = async (data) => {
  try {
    // Validate required fields
    const requiredFields = ['title', 'content', 'category', 'priority', 'postedBy', 'postedByName']

    for (const field of requiredFields) {
      if (!data[field]) {
        return { success: false, error: `Missing required field: ${field}` }
      }
    }

    // Validate content length (max 2000 characters)
    if (data.content.length > 2000) {
      return { success: false, error: 'Content exceeds maximum length of 2000 characters' }
    }

    // Validate title length (max 200 characters)
    if (data.title.length > 200) {
      return { success: false, error: 'Title exceeds maximum length of 200 characters' }
    }

    // Add timestamps
    const now = Timestamp.now()
    const announcementData = {
      ...data,
      postedAt: now,
      updatedAt: now,
      createdAt: new Date().toISOString()
    }

    // Create document
    const docRef = await addDoc(collection(db, 'announcements'), announcementData)

    return {
      success: true,
      docId: docRef.id,
      data: {
        id: docRef.id,
        ...announcementData
      }
    }
  } catch (error) {
    console.error('Create announcement error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Update existing announcement
 * @param {string} id - Announcement document ID
 * @param {object} data - Updated announcement data
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const updateAnnouncement = async (id, data) => {
  try {
    // Validate content length
    if (data.content && data.content.length > 2000) {
      return { success: false, error: 'Content exceeds maximum length of 2000 characters' }
    }

    // Validate title length
    if (data.title && data.title.length > 200) {
      return { success: false, error: 'Title exceeds maximum length of 200 characters' }
    }

    // Update timestamp
    const updateData = {
      ...data,
      updatedAt: Timestamp.now()
    }

    await updateDoc(doc(db, 'announcements', id), updateData)

    return { success: true }
  } catch (error) {
    console.error('Update announcement error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Delete announcement
 * @param {string} id - Announcement document ID
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const deleteAnnouncement = async (id) => {
  try {
    await deleteDoc(doc(db, 'announcements', id))
    return { success: true }
  } catch (error) {
    console.error('Delete announcement error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Helper: Format Firestore timestamp to readable date
 * @param {Timestamp|Date|string} timestamp - Firestore timestamp, Date object, or ISO string
 * @returns {string} - Formatted date
 */
export const formatAnnouncementDate = (timestamp) => {
  if (!timestamp) return 'Unknown'

  try {
    let date

    // Handle Firestore Timestamp object
    if (timestamp && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate()
    }
    // Handle Date object
    else if (timestamp instanceof Date) {
      date = timestamp
    }
    // Handle ISO string or timestamp number
    else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      date = new Date(timestamp)
    }
    // Unknown format
    else {
      return 'Unknown'
    }

    // Validate date
    if (isNaN(date.getTime())) {
      return 'Unknown'
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    console.error('Format date error:', error)
    return 'Unknown'
  }
}

/**
 * Helper: Get category badge variant
 * @param {string} category - Category name
 * @returns {string} - Badge variant
 */
export const getCategoryBadgeVariant = (category) => {
  const variants = {
    'General': 'info',
    'HR': 'success',
    'IT': 'warning',
    'Urgent': 'error'
  }
  return variants[category] || 'info'
}

/**
 * Helper: Get list of category options
 * @returns {array} - Array of category names
 */
export const getCategoryOptions = () => {
  return ['General', 'HR', 'IT', 'Urgent']
}

/**
 * Helper: Get list of priority options
 * @returns {array} - Array of priority names
 */
export const getPriorityOptions = () => {
  return ['Normal', 'Urgent']
}

/**
 * Helper: Truncate content for preview
 * @param {string} content - Full content
 * @param {number} maxLength - Maximum length (default 150)
 * @returns {string} - Truncated content
 */
export const truncateContent = (content, maxLength = 150) => {
  if (!content || content.length <= maxLength) {
    return content
  }
  return content.substring(0, maxLength).trim() + '...'
}

/**
 * Get count of recent announcements (last 7 days)
 * @returns {Promise<{ success: boolean, count: number, error?: string }>}
 */
export const getRecentAnnouncementsCount = async () => {
  try {
    // Calculate 7 days ago
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
    sevenDaysAgo.setHours(0, 0, 0, 0)

    const sevenDaysAgoTimestamp = Timestamp.fromDate(sevenDaysAgo)

    // Query announcements posted in last 7 days
    const announcementsQuery = query(
      collection(db, 'announcements'),
      where('postedAt', '>=', sevenDaysAgoTimestamp)
    )

    const querySnapshot = await getDocs(announcementsQuery)
    return { success: true, count: querySnapshot.size }
  } catch (error) {
    console.error('Get recent announcements count error:', error)
    return { success: false, count: 0, error: error.message }
  }
}
